import isEmpty from 'lodash/isEmpty';
import Cookies from 'js-cookie';

// Global utils
import analytics from 'utils/analytics';

// Request utils
import {authCheckRequest, logoutUserRequest} from 'request/auth';

// Redux
import {store} from 'store';
import {clearUserData, setAutoSyncRunning, setConnectedToUser, setDeepSyncRunning, setLastSession, setManualSyncRunning, setQuickSyncRunning, setSession, setSessionWhitelist, setUserData} from 'store/actions';
import {sendAuthTokenToElectron} from './electron';
import {isWebClient} from './buildMode';
import {disconnectOnlineSocket} from 'utils/socket';

class AuthUtil {
	constructor() {
		this.sessionKey = 'SES';
		this.onlineCredentialResolver = null;
		this.onlineLoginHandler = null;
		this.googleScriptPromise = null;
		this.googleIdentityInitialized = false;
	}
	get sessionStorageKey() {
		return this.sessionKey;
	}
	get sessionToken() {
		return Cookies.get(this.sessionKey);
	}
	setOnlineCredentialResolver(resolver) {
		this.onlineCredentialResolver = resolver;
	}
	consumeOnlineCredential(credential) {
		if (!this.onlineCredentialResolver) return false;
		this.onlineCredentialResolver(credential);
		this.onlineCredentialResolver = null;
		return true;
	}
	loadGoogleIdentityScript() {
		if (window.google?.accounts?.id) return Promise.resolve();
		if (!this.googleScriptPromise) {
			this.googleScriptPromise = new Promise((resolve, reject) => {
				let script = document.getElementById('google-identity-services');
				const onLoaded = () => window.google?.accounts?.id
					? resolve()
					: reject(new Error('Google sign-in could not load.'));
				if (!script) {
					script = document.createElement('script');
					script.id = 'google-identity-services';
					script.src = 'https://accounts.google.com/gsi/client';
					script.async = true;
					script.defer = true;
				}
				script.addEventListener('load', onLoaded, {once: true});
				script.addEventListener('error', () => reject(new Error('Google sign-in could not load.')), {once: true});
				if (!script.isConnected) document.head.appendChild(script);
			});
			this.googleScriptPromise = this.googleScriptPromise.catch(error => {
				this.googleScriptPromise = null;
				throw error;
			});
		}
		return this.googleScriptPromise;
	}
	initializeOnlineGoogleIdentity(onCredential) {
		if (onCredential) this.onlineLoginHandler = onCredential;
		if (this.googleIdentityInitialized) return;
		const googleIdentity = window.google?.accounts?.id;
		if (!googleIdentity) throw new Error('Google sign-in is not available.');
		const clientId = process.env.REACT_APP_FRESTO_GOOGLE_CLIENT_ID;
		if (!clientId) throw new Error('Google sign-in is not configured for this build.');
		googleIdentity.initialize({
			client_id: clientId,
			callback: ({credential}) => {
				if (!this.consumeOnlineCredential(credential)) this.onlineLoginHandler?.(credential);
			},
		});
		this.googleIdentityInitialized = true;
	}
	setOnlineLocation(location, canSwitchLocation) {
		if (!location) return;
		store.dispatch(setUserData({
			selectedLocation: {id: location.id, label: location.name, timezone: location.timezone},
			...(canSwitchLocation !== undefined && {canSwitchLocation: !!canSwitchLocation}),
		}));
	}
	async acquireOnlineCredential() {
		await this.loadGoogleIdentityScript();
		this.initializeOnlineGoogleIdentity();
		return new Promise((resolve, reject) => {
			const timeout = setTimeout(() => {
				this.onlineCredentialResolver = null;
				reject(new Error('Google sign-in was not completed. Please sign in again.'));
			}, 5000);
			this.setOnlineCredentialResolver(credential => {
				clearTimeout(timeout);
				resolve(credential);
			});
			window.google.accounts.id.prompt();
		});
	}
	async isSessionValid() {
		analytics.setUserProperties();
		if (!isEmpty(this.sessionToken)) {
			const result = await authCheckRequest();
			if (isWebClient) {
				// /auth/me returns {principal, location} unwrapped; also accept a {data: {...}} envelope.
				const session = result?.data?.principal ? result.data : (result || {});
				const location = session.location || {};
				store.dispatch(setUserData({
					username: session.principal?.email,
					selectedLocation: {id: location.id, label: location.name, timezone: location.timezone},
					principal: session.principal,
					canSwitchLocation: !!session.canSwitchLocation,
				}));
				return result;
			}
			store.dispatch(setConnectedToUser({connected: result?.data?.isSocketConnected || false}));
			if (!isEmpty(result?.data?.lastSession)) {
				store.dispatch(setSession({session: result?.data?.session} || {}));
				store.dispatch(setLastSession({lastSession: result?.data?.lastSession} || {}));
			}
			store.dispatch(setSessionWhitelist({sessionWhitelist: result?.data?.sessionWhitelist} || false));
			sendAuthTokenToElectron(this.sessionToken);
			if (!isEmpty(result?.data?.runningSync)) {
				const runningSync = result?.data?.runningSync;
				store.dispatch(setAutoSyncRunning({autoSyncRunning: runningSync?.autoSync} || false));
				store.dispatch(setDeepSyncRunning({deepSyncRunning: runningSync?.deepSync} || false));
				store.dispatch(setQuickSyncRunning({quickSyncRunning: runningSync?.quickSync} || false));
				store.dispatch(setManualSyncRunning({manualSyncRunning: runningSync?.manualSync} || false));
			}
			return result;
		} else {
			sendAuthTokenToElectron(false);
			return Promise.reject(new Error('TOKEN_DONT_EXIST'));
		}
	}
	revokeCurrentSession(setState) {
		if (isWebClient) {
			Cookies.remove(this.sessionKey);
			store.dispatch(clearUserData());
			disconnectOnlineSocket();
			return Promise.resolve();
		}
		return new Promise((resolve, reject) => {
			logoutUserRequest(setState)
				.then(resolve)
				.catch(reject)
				.finally(() => {
					Cookies.remove(this.sessionKey);
					store.dispatch(clearUserData());
				});
		});
	}
	async setNewSession(token) {
		return await Cookies.set(this.sessionKey, token, {
			expires: 30,
		});
	}
}

export default new AuthUtil();
